import { Dictionary } from "@ngrx/entity"
import { createSelector } from "@ngrx/store"
import { linkedSectionHasChanges, makeBudgetReport } from "../../helpers/budget-helpers"
import { PaymentGridData, BudgetSectionGridData, CashFlowGridData } from "../../helpers/grid-helpers"
import { getBeginningOfMonthDate,  } from "../../helpers/helpers"
import { getSet, jsonCopyObject, makeMapFromListOfObjectsAndKey } from "globals/helpers/helpers"
import { attachedToCountByAssetIdReselector } from "./assets.reselector"
import { BudgetQuoteSectionProps, BudgetSection, BudgetSummary, createEmptySection, Budget, AccountBudgetMonthSummary, createEmptyAccountBudgetMonthlySummary, SimpleGridCol, BudgetSummaryTaxType } from "./budget.data"
import { scheduleToScheduleGridData, getGridDataFromSection } from "./budget.reselector.helpers"
import { tasksReselector } from "./tasks.reselector"
import { eventStateSelector, JoiEvent } from 'src/app/store/events.era';
import { budgetColumns, BudgetTableData } from "../../helpers/table-column-types"
import { getEventStatus } from "./events.reselector"
import { showBudgetReselector } from "./permissions.reselector"
import { makePrettyNumber } from "../../services/pipes/prettyNumbers.pipe"
import { Task } from "globals/store/tasks.data"
import { Asset } from "globals/store/assets.data"
import { budgetStateSelector } from "../budget.era"
import { accountStateSelector } from "../accounts.era"
import { tasksStateSelector } from "../tasks.era"
import { assetsStateSelector } from "../assets.era"
import { settingsStateSelector } from "../settings.era"
import { filterStateSelector } from "../filters.era"

const event = createSelector(eventStateSelector, (state) => state.event)
const events = createSelector(eventStateSelector, (state) => state.entities)
const account = createSelector(accountStateSelector, (state) => state.account)
const budget = createSelector(budgetStateSelector, (state) => state.budget)
const budgetStartDate = createSelector(budgetStateSelector, (state) => state.summaryStartDate)
const budgetTaxType = createSelector(budgetStateSelector, (state) => state.summaryTaxType)

const budgets = createSelector(budgetStateSelector, (state) => state.budgets)
const tasks = createSelector(tasksStateSelector, (state) => state.entities)
const assets = createSelector(assetsStateSelector, (state) => state.entities)
const selectedBudgetAccount = createSelector(accountStateSelector, (state) => state.selectedBudgetAccount)
const budgetFilters = createSelector(filterStateSelector, (state) => state.budgets)
const showClosedBudgets = createSelector(settingsStateSelector, (state) => state.user.showClosedBudgets)

export const accountBudgetsReselector = createSelector(
  selectedBudgetAccount,
  budgets,
  (accountId, budgets): Budget[] => {
    if (!accountId) return []
    return Object.values(jsonCopyObject(budgets)).filter((b: Budget) => b.accountId === accountId) as Budget[]
  }
)


export const accountBudgetsSummaryColumnsReselector = createSelector(
  budgetStartDate,
  (startDate): SimpleGridCol[] => {
    const columns: SimpleGridCol[] = []
    Array.from({ length: 12 }).forEach((_, i) => {
      const date = new Date(startDate)
      date.setMonth(date.getMonth() + i)
      columns.push({
        title: `${date.getMonth() + 1}/${date.getFullYear()%100}`,
        key: date.getTime()
      })
    })
    columns.push({ title: 'Total', key: 'total' })
    // console.log({ startDate, columns })
    return columns
  }
)

export const accountBudgetSummariesPreselector = createSelector(
  accountBudgetsReselector,
  events,
  budgetTaxType,
  (budgets, events, budgetTaxType: BudgetSummaryTaxType): Dictionary<AccountBudgetMonthSummary> => {

    const monthlySummaries: Dictionary<AccountBudgetMonthSummary> = {}
    budgets.forEach((b: Budget) =>  {
      const { sections, revenuePaymentSchedule, costIds, eventId } = b
      const { totalRevenue, totalCosts } = makeBudgetReport({ ...b, summaryTaxInclusive: budgetTaxType === 'both' ? b.summaryTaxInclusive : budgetTaxType === 'inclusive' ? true : false })

      const event = events[eventId]
      if (!event || !event.dates?.length) return null
      const eventStatus = getEventStatus(event)

      const monthTime = getBeginningOfMonthDate(new Date(event.dates[0])).getTime()
      if (!monthlySummaries[monthTime]) monthlySummaries[monthTime] = createEmptyAccountBudgetMonthlySummary()
      const summary = monthlySummaries[monthTime] // make sure to mutate this, not replace it

      summary.budgetsUsed++
      summary.totalRevenue += totalRevenue
      summary.totalCosts += totalCosts
      switch (eventStatus) {
        case 'Completed':
        case 'Cancelled': // It doesn't make much sense to put cancelled in completed when you can easily toggle it now...
          summary.completedRevenue += totalRevenue
          summary.completedCosts += totalCosts
          break
        case 'Confirmed':
          summary.confirmedRevenue += totalRevenue
          summary.confirmedCosts += totalCosts
          break
        case 'Pending':
          summary.pendingRevenue += totalRevenue
          summary.pendingCosts += totalCosts
          break
      }

      summary.profit = summary.totalRevenue - summary.totalCosts
      summary.profitMargin = (!summary.profit || !summary.totalRevenue ? 0 : summary.profit / summary.totalRevenue) * 100

      revenuePaymentSchedule.items.filter(i => i.date && i.amount).forEach(({ date, amount, status }) => {
        const monthTime = getBeginningOfMonthDate(new Date(date)).getTime()
        if (!monthlySummaries[monthTime]) monthlySummaries[monthTime] = createEmptyAccountBudgetMonthlySummary()
        switch (status) {
          case 'Paid':
            monthlySummaries[monthTime].revenuePaid += amount
            break
          case 'Pending':
            monthlySummaries[monthTime].revenuePending += amount
            break
        }
      })
      
      costIds.map(id => sections[id]?.schedule).filter(s => s).forEach(s => s.items.filter(i => i.date && i.amount).forEach(({ date, amount, status }) => {
        const monthTime = getBeginningOfMonthDate(new Date(date)).getTime()
        if (!monthlySummaries[monthTime]) monthlySummaries[monthTime] = createEmptyAccountBudgetMonthlySummary()
        switch (status) {
          case 'Paid':
            monthlySummaries[monthTime].billsPaid += amount
            break
          case 'Pending':
            monthlySummaries[monthTime].billsPending += amount
            break
        }
      }))
        
    }) // end budget

    Object.values(monthlySummaries).forEach(s => // need to do this last
      s.cashFlow = s.revenuePaid + s.revenuePending - s.billsPaid - s.billsPending
    )

    return monthlySummaries
  }
)

export const accountBudgetSummariesReselector = createSelector(
  accountBudgetSummariesPreselector,
  accountBudgetsSummaryColumnsReselector,
  (summaries, columns) => {
    const totals: AccountBudgetMonthSummary = createEmptyAccountBudgetMonthlySummary()
    const usedSummaries = columns.filter(c => c.key !== 'total').map(c => summaries[c.key]).filter(s => s)
    // console.log({ summaries, usedSummaries, totals, columns })

    usedSummaries.forEach(({ completedRevenue, confirmedRevenue, pendingRevenue, totalRevenue, completedCosts, confirmedCosts, pendingCosts, totalCosts, profit, profitMargin, revenuePaid, revenuePending, billsPaid, billsPending, cashFlow, budgetsUsed }) => {
      totals.completedRevenue += completedRevenue
      totals.confirmedRevenue += confirmedRevenue
      totals.pendingRevenue += pendingRevenue
      totals.totalRevenue += totalRevenue
      totals.completedCosts += completedCosts
      totals.confirmedCosts += confirmedCosts
      totals.pendingCosts += pendingCosts
      totals.totalCosts += totalCosts
      totals.profit += profit
      totals.revenuePaid += revenuePaid
      totals.revenuePending += revenuePending
      totals.billsPaid += billsPaid
      totals.billsPending += billsPending
      totals.cashFlow += cashFlow
      totals.budgetsUsed += budgetsUsed
    })

    totals.profitMargin = (!totals.profit || !totals.totalRevenue ? 0 : totals.profit / totals.totalRevenue) * 100

    summaries.total = totals
    // console.log({ summaries })
    return summaries
  }
)


export const budgetsTableReselector = createSelector(
  accountBudgetsReselector,
  events,
  budgetFilters,
  showClosedBudgets,
  (budgets, events, filters, showClosedBudgets) => {

    const searchTerm = filters.search ? filters.search.toLocaleUpperCase() : null
    const tableData: BudgetTableData[] = budgets.map(b => {
      const event = events[b.eventId]
      if (!event || (!showClosedBudgets && !!event.closed)) return null

      const date = event.dates?.length ? new Date(event.dates[0]) : null
      const status = getEventStatus(event)
      const statusText = status === 'Completed' ? 'Closed' : status

      const budgetSummary = makeBudgetReport(b)

      return {
        id: b.eventId,
        [budgetColumns.eventName]: { text: event.title || '' },
        [budgetColumns.startDate]: { HHMMText: null, date },
        [budgetColumns.eventStatus]: { text: statusText, date,  },
        [budgetColumns.currency]: { text: b.currency },
        [budgetColumns.revenue]: { text:  makePrettyNumber(budgetSummary.totalRevenue) },
        [budgetColumns.costs]: { text:  makePrettyNumber(budgetSummary.totalCosts) },
        [budgetColumns.taxType]: { text: b.summaryTaxInclusive ? 'Inclusive' : 'Exclusive' },
        [budgetColumns.profit]: { text:  makePrettyNumber(budgetSummary.profit) },
        [budgetColumns.profitPercent]: { text: !b.totalRevenue ? 'N/A' : `${makePrettyNumber(budgetSummary.profitMargin*100)}` },
      }
    }).filter(d => d && (!searchTerm || d.eventName.text.toLocaleUpperCase().includes(searchTerm)))
    const tableRowsById = makeMapFromListOfObjectsAndKey(tableData, 'id')
    return { tableData, tableRowsById }
  }
)


export const taskBudgetQuantitiesByIdReselector = createSelector(
  tasksReselector,
  (taskList): Dictionary<number> => {
    const taskBudgetQuantiesById: Dictionary<number> = {}
    taskList.filter((t: Task) => t.estimatedTime).forEach((t: Task) => {
      const { h, m } = t.estimatedTime
      taskBudgetQuantiesById[t.id] = h + m / 60
    })
    return taskBudgetQuantiesById
  }
)

export const hasChangedBudgetAssetsReselector = createSelector(
  budget,
  assets,
  (budget, assets): boolean => {
    if (!budget || !budget.sections.assets) return false
    return budget.sections.assets.lineItems.some(l => {
      const asset = assets[l.id]
      if (!asset || !asset.showInBudget) return true
      if (asset.quantity && l.quantity !== asset.quantity) return true
      if (!asset.quantity && l.quantity !== 1) return true // 1 is default
    }) // catches deleted assets or unlinked assets
  }
)

export const hasChangedBudgetTasksReselector = createSelector(
  budget,
  taskBudgetQuantitiesByIdReselector,
  (budget, taskBudgetQuantiesById): boolean => {
    if (!budget || !budget.sections.tasks) return false
    return budget.sections.tasks.lineItems.some(l => l.quantity !== taskBudgetQuantiesById[l.id]) // should catch deleted tasks and changed quantities
  }
)

export const costAssetsToggleSectionReselector = createSelector(
  budget,
  account,
  assets,
  (budget, account, assets): BudgetSection => {
    if (!budget) return null
    const assetsSection: BudgetSection = budget.sections.assets
    
    const activeIds: Set<string> = getSet(assetsSection?.lineItems, 'id')
    const unusedAssetsList = Object.values(assets).filter(a => !activeIds.has(a.id) && a.showInBudget)

    let section: BudgetSection
    if (assetsSection) {
      section = jsonCopyObject(assetsSection)
    } else {
      section = createEmptySection(account, true, 'assets', 0)
      section.name = 'ASSETS'
    }

    unusedAssetsList.forEach(a => section.lineItems.push({
      id: a.id,
      item: '',
      description: a.name,
      quantity: a.quantity || 1, // Rob wanted default 1
      unitCost: 0,
      recurrence: 1,
      status: 'Estimate',
      total: 0,
    }))

    return section
  }
)

export const costTasksToggleSectionReselector = createSelector(
  budget,
  account,
  tasks,
  taskBudgetQuantitiesByIdReselector,
  (budget, account, tasks, taskBudgetQuantatiesById): BudgetSection => {
    if (!budget) return null
    const tasksSection = budget.sections.tasks

    
    const activeIds: Set<string> = getSet(tasksSection?.lineItems, 'id')
    const unusedTaskList = Object.values(tasks).filter(t => !activeIds.has(t.id) && taskBudgetQuantatiesById[t.id])

    let section: BudgetSection
    if (tasksSection) {
      section = jsonCopyObject(tasksSection)
    } else {
      section = createEmptySection(account, false, 'tasks', 0)
      section.name = 'TASKS'
    }

    unusedTaskList.forEach(t => section.lineItems.push({
      id: t.id,
      item: '',
      description: t.name,
      quantity: taskBudgetQuantatiesById[t.id],
      unitCost: 0,
      recurrence: 1,
      status: 'Estimate',
      total: 0,
    }))

    return section
  }
)

export const canAccessBudgetReselector = createSelector(
  showBudgetReselector,
  (data) => !!data
)

export const viewBudgetReselector = createSelector(
  event,
  canAccessBudgetReselector,
  (event, showBudget) => {
    if (!event) return null
    else return showBudget ? { event, show: true } : { event, show: false }
  }
)

export const revenuePaymentsReselector = createSelector(
  budget,
  (budget): PaymentGridData[] => !budget?.revenuePaymentSchedule ? [] : scheduleToScheduleGridData(budget.revenuePaymentSchedule, budget.totalRevenue)
)

export const costSchedulesReselector = createSelector(
  budget,
  (budget): Dictionary<PaymentGridData[]> => {
    const gridDatas: Dictionary<PaymentGridData[]> = {}
    if (!budget) return gridDatas
    
    budget.costIds.filter(id => budget.sections[id].schedule).forEach(id => gridDatas[id] = scheduleToScheduleGridData(budget.sections[id].schedule, budget.sections[id].total))
    return gridDatas
  }
)

export const budgetSummaryReselector = createSelector(
  budget,
  makeBudgetReport
)


export const makeCashFlowGrid = (summary: BudgetSummary, budget: Budget): CashFlowGridData[] => summary?.cashFlow?.map(({ date, description, received, billed, status, balance, costSectionId }) => ({
  id: null,
  description: { text: description },
  date: { date: new Date(date) },
  received: { amount: received },
  billed: { amount: billed },
  status: { status },
  source: { text: !costSectionId ? 'Revenue' : budget.sections[costSectionId] ? 
    (budget.sections[costSectionId].name || 'Unnamed Cost Section') : budget.quotes[costSectionId] ?
    (budget.quotes[costSectionId].name || 'Unnamed Linked Quote') : 'Deleted Section' },
  balance: { amount: Math.abs(balance), inRed: balance < 0 }
}))

export const budgetCashFlowGridReselector = createSelector(
  budgetSummaryReselector,
  budget,
  makeCashFlowGrid  
)

export const linkableSectionHasChangesMapReselector = createSelector(
  budget,
  event,
  (budget, event: JoiEvent): Dictionary<BudgetSection> => {
    const linkedChangedSectionsMap = {}
    if (!budget || !event) return linkedChangedSectionsMap

    const propsToIgnore = [...['dialogName', 'isQuote', 'linkedCache', 'id'], ...BudgetQuoteSectionProps] // id is included because it can change from tasks / assets to a random id when the single tasks / asset section is unlinked in costs

    Object.values(budget.sections)
      .filter(s => !!s.linkId && linkedSectionHasChanges(!s.isQuote, s.isQuote ? ( budget.quotes[s.linkId] ) : budget.sections[s.linkId], s.linkedCache, propsToIgnore))
      .forEach(s => {
        if (s.isQuote) {
          const quote = budget.quotes[s.linkId]
          linkedChangedSectionsMap[s.id] = !quote || !event.resources[quote.resourceId] ? true : quote
        } else linkedChangedSectionsMap[s.id] = budget.sections[s.linkId]
      })
    return linkedChangedSectionsMap
  }
)

export const linkableCostSectionsReselector = createSelector(
  budget,
  (budget): BudgetSection[] => {
    if (!budget) return []
    const allLinkIds = getSet(budget.revenueIds.map(id => budget.sections[id]), 'linkId')
    return budget.costIds.filter(id => !allLinkIds.has(id)).map(id => budget.sections[id])
  }
)

export const sectionsReselector = createSelector(
  budget,
  tasks,
  taskBudgetQuantitiesByIdReselector,
  assets,
  (budget, tasks, taskBudgetQuantiesById, assets): Dictionary<BudgetSectionGridData[]> => {
    if (!budget) return {}
    const gridDatas: Dictionary<BudgetSectionGridData[]> = {}

    const allIds = [...budget.revenueIds, ...budget.costIds]
    allIds.map(id => budget.sections[id]).filter(s => s).forEach(section => {
      const gridData: BudgetSectionGridData[] = getGridDataFromSection(section, budget.currency)
      gridDatas[section.id] = gridData
    })
    if (gridDatas.tasks) {
      gridDatas.tasks.forEach(r => {
        const task = tasks[r.id]
        if (!task) {
          r.qtyUpdated = { amount: 0 }
          r.actionButton = 'DELETE'
          return
        }

        const newQuantity = taskBudgetQuantiesById[r.id]

        r.qtyUpdated = !newQuantity ? null : { amount: newQuantity }
        if (newQuantity !== r.qty.amount) r.actionButton = 'UPDATE'
      })
    }

    if (gridDatas.assets) {
      gridDatas.assets.forEach(s => {
        const asset = assets[s.id]
        if (!asset || !asset.showInBudget) {
          s.qtyUpdated = { amount: 0 }
          s.actionButton = 'DELETE'
          return
        }
        const newQuantity = asset.quantity
        s.qtyUpdated = { amount: newQuantity || 1 }
        if ((newQuantity && newQuantity !== s.qty.amount) || (!newQuantity && s.qty.amount !== 1)) s.actionButton = 'UPDATE'
      })
    }


    // console.log({ gridDatas })
    return gridDatas
  }
)
